import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type {
    ObjectCardContextType,
    ObjectCardInfoType,
    ObjectCardProviderProps,
    ObjectCardType,
    ObjectPayStatusSyncType,
} from '@econtractor/types';
import * as apiObject from 'api/apiObjects';
import useSettingsParams from 'hooks/useSettingsParams';
import { isObjectEditLocked } from 'utils/helpers/objects/isObjectEditLocked';
import vconst from 'utils/helpers/structs/vconst';
import { _localGet, _localSet } from 'utils/helpers/gimHelper';

const SHOW_ANALYTICS_STORAGE_KEY = 'objectCardShowAnalytics';

const readShowAnalytics = (): boolean => {
    try {
        const raw = _localGet(SHOW_ANALYTICS_STORAGE_KEY) as string | null | undefined;
        if (raw === null || typeof raw === 'undefined') return true;
        return raw === '1';
    } catch {
        return true;
    }
};

const ObjectCardContext = createContext<ObjectCardContextType | null>(null);

export const useObjectCard = (): ObjectCardContextType =>
    useContext(ObjectCardContext) as ObjectCardContextType;

interface ObjectCardProviderComponentProps extends Omit<ObjectCardProviderProps, 'children'> {
    children?: ReactNode;
}

export const ObjectCardProvider = ({
    objectId,
    onlySale: onlySaleProp = false,
    onlyService: onlyServiceProp = false,
    children,
}: ObjectCardProviderComponentProps) => {
    const params = useSettingsParams();
    const onlyService = Boolean(onlyServiceProp);
    const onlySale = Boolean(onlySaleProp) && !onlyService;
    const isSaleLike = onlySale || onlyService;

    const [vObject, setVObject] = useState<ObjectCardType | null>(null);
    const [showSpinner, setShowSpinner] = useState(false);
    const [vObjectInfo, setVObjectInfo] = useState<ObjectCardInfoType | null>(null);
    const [showAnalytics, setShowAnalyticsState] = useState(readShowAnalytics);
    const [documentsRevision, setDocumentsRevision] = useState(0);

    const setShowAnalytics: ObjectCardContextType['setShowAnalytics'] = (next) => {
        setShowAnalyticsState((prev) => {
            const value = typeof next === 'function' ? next(prev) : Boolean(next);
            try {
                _localSet(SHOW_ANALYTICS_STORAGE_KEY, value ? '1' : '0');
            } catch {
                /* ignore storage errors */
            }
            return value;
        });
    };

    const touchDocuments = () => {
        setDocumentsRevision((version) => version + 1);
    };

    const applyPayStatusSync = async (targetObjectId: number) => {
        if (!params.objectPaymentControl || !params.autoSyncPayStatus) return;
        const sync: ObjectPayStatusSyncType | null = await apiObject.syncPayStatusIfCovered(targetObjectId);
        if (!sync?.updated) return;
        setVObject((prev) => {
            if (!prev?.object) return prev;
            return {
                ...prev,
                object: {
                    ...prev.object,
                    objectPayStatus: sync.payStatus,
                },
            };
        });
    };

    const fetchObject = async () => {
        if (!objectId) {
            setVObject(null);
            return;
        }
        setShowSpinner(true);
        const resObject: ObjectCardType | null = await apiObject.getObject(objectId);
        setVObject(resObject);
        setShowSpinner(false);
        if (resObject?.object?.ID) {
            void applyPayStatusSync(objectId);
        }
    };

    useEffect(() => {
        let prodPrice = 0;
        let prodPriceRate = 0;
        let prodPriceSale = 0;
        let prodPriceSaleRate = 0;
        vObject?.operations
            ?.filter((o) => o.operStatus === vconst.operStatApproved)
            ?.map((o) => {
                prodPrice = prodPrice + Number(o.operPrice) * Number(o.operCount);
                prodPriceRate = prodPriceSale + Number(o.operPriceRate) * Number(o.operCount);
                prodPriceSale = prodPriceSale + Number(o.operPriceSale) * Number(o.operCount);
                prodPriceSaleRate = prodPriceSaleRate + Number(o.operPriceSaleRate) * Number(o.operCount);
            });

        setVObjectInfo({
            prodPrice,
            prodPriceRate,
            prodPriceSale,
            prodPriceSaleRate,
        });
    }, [vObject]);

    const isEditLocked = useMemo(
        () => isObjectEditLocked(vObject?.object?.objectStatus, params) || vObject?.object?.myAccess === 'view',
        [vObject?.object?.objectStatus, vObject?.object?.myAccess, params.lockFinishedObject],
    );

    const value: ObjectCardContextType = {
        objectId,
        vObjectInfo,
        vObject,
        setVObject,
        fetchObject,
        showSpinner,
        setShowSpinner,
        showAnalytics,
        setShowAnalytics,
        isEditLocked,
        onlySale,
        onlyService,
        isSaleLike,
        documentsRevision,
        touchDocuments,
    };

    return (
        <ObjectCardContext.Provider value={value}>
            {children}
        </ObjectCardContext.Provider>
    );
};
